import { useLocation, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Home, ShieldOff } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Unified "Access Denied" page rendered when ProtectedRoute determines
 * the current user lacks the required permission/role for the route.
 *
 * Rendered in-place by ProtectedRoute (no navigate-redirect) so the URL
 * stays at whatever the user tried to access — preserves back-button
 * intuition and lets the user see exactly which path was blocked. When
 * a route is composed with one of the `withProtection*` helpers in
 * router.tsx, the fallback is wrapped in DashboardLayout, so the
 * sidebar/header shell stays visible.
 */
export function AccessDeniedPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // If the user arrived via direct link / external referrer there is no
  // useful history entry to step back to — fall through to /dashboard so
  // the button never looks broken.
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    void navigate({ to: '/dashboard' });
  };

  return (
    <div
      data-testid="access-denied-page"
      className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center"
    >
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <ShieldOff className="h-8 w-8" aria-hidden />
      </div>
      <h1 className="mb-2 text-3xl font-bold tracking-tight">Access Denied</h1>
      <p className="mb-1 max-w-md text-muted-foreground">
        You don&apos;t have permission to view this page.
      </p>
      <p className="mb-8 max-w-md text-sm text-muted-foreground">
        Tried to access{' '}
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
          {location.pathname}
        </code>
        . If you believe this is a mistake, contact your administrator.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button data-testid="access-denied-back" variant="outline" onClick={goBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go back
        </Button>
        <Button
          data-testid="access-denied-dashboard"
          onClick={() => void navigate({ to: '/dashboard' })}
        >
          <Home className="mr-2 h-4 w-4" />
          Go to Dashboard
        </Button>
      </div>
    </div>
  );
}
